import React, { useEffect, useState } from "react"
import { IconCalendarEvent } from "@tabler/icons-react"
import { IEvent } from "@dc-ireland/shared"
import apiClient from "../../lib/api"
import { resolveImageUrl } from "../../lib/imageUtils"
import LandingCarousel from "./LandingCarousel"
import LandingEmptyState from "./LandingEmptyState"
import LandingEventCard from "./LandingEventCard"
import LandingReveal from "./LandingReveal"
import LandingSectionHeader from "./LandingSectionHeader"

const formatBadge = (start: Date, location?: string) => {
  const datePart = start
    .toLocaleDateString("en-IE", { month: "short", day: "numeric" })
    .toUpperCase()
  const locationPart = location?.toUpperCase() ?? "ONLINE"
  return `${datePart} • ${locationPart}`
}

type EventCardProps = {
  id: string
  title: string
  badge: string
  description?: string
  imageUrl?: string
  ctaLabel?: string
  registerUrl?: string
  showCardText?: boolean
}

const toEventCardProps = (event: IEvent, index: number): EventCardProps => {
  const start = new Date(event.startDate)
  return {
    id: event.id ?? `${event.title}-${index}`,
    title: event.title,
    badge: formatBadge(start, event.location),
    description: event.description,
    imageUrl: resolveImageUrl(event.imageUrl),
    ctaLabel: event.registerLabel,
    registerUrl: event.registerUrl,
    showCardText: event.showCardText,
  }
}

const cardWrapperClass = "w-[min(calc(100vw-2rem),64rem)] shrink-0 snap-start"

const LandingEventsSection: React.FC = () => {
  const [events, setEvents] = useState<IEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await apiClient.getEvents()
        const now = new Date()
        const upcoming = data.filter((e) => new Date(e.startDate) >= now)
        setEvents(upcoming)
      } catch (err) {
        console.error("Error fetching events:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [])

  const displayEvents = events.map(toEventCardProps)

  const renderCards = () =>
    displayEvents.map(({ id, title, badge, description, imageUrl, ctaLabel, registerUrl, showCardText }) => (
      <div key={id} className={cardWrapperClass}>
        <LandingEventCard
          title={title}
          badge={badge}
          description={description}
          imageUrl={imageUrl}
          ctaLabel={ctaLabel}
          registerUrl={registerUrl}
          showCardText={showCardText}
        />
      </div>
    ))

  return (
    <section id="upcoming-gatherings" className="relative scroll-mt-24 overflow-hidden bg-[#fafafa] py-20">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(214,229,255,0.35),transparent_55%)]" />

      <div className="relative mx-auto flex max-w-6xl flex-col items-center px-4 md:px-8 lg:px-20">
        <LandingSectionHeader
          eyebrow="What's happening"
          title="Upcoming Programs"
          description="Join us in person or online as we cultivate an atmosphere of God's presence for our transformation."
        />
      </div>

      {loading ? (
        <div className="relative mt-14 flex w-full justify-center py-12">
          <span className="loading loading-spinner loading-lg text-dc-primary-900" />
        </div>
      ) : displayEvents.length === 0 ? (
        <div className="relative mt-14">
          <LandingEmptyState
            pattern="calendar"
            icon={IconCalendarEvent}
            title="Something beautiful is being prepared"
            description="No upcoming gatherings on the calendar right now. Follow us on social media or check back soon — we'd love to see you at the next one."
            action={
              <a
                href="https://www.instagram.com/dominioncitydublin"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg border border-dc-primary-900/20 px-5 py-2.5 font-dm-sans text-sm font-semibold tracking-wide text-dc-primary-900 transition hover:border-dc-primary-900/40 hover:bg-dc-primary-900/5"
              >
                Follow us on Instagram
              </a>
            }
          />
        </div>
      ) : (
        <LandingReveal delay={0.1} className="relative mx-auto mt-14 w-full max-w-6xl px-4 md:px-8 lg:px-16">
          <LandingCarousel ariaLabel="Upcoming programs carousel">{renderCards()}</LandingCarousel>
        </LandingReveal>
      )}
    </section>
  )
}

export default LandingEventsSection
